import type {Ref} from 'vue';
import type {RouteLocationNormalized} from 'vue-router';
import {useGtm} from '@gtm-support/vue-gtm';
import {storeToRefs} from 'pinia';
import type {Gsoa_ProductCatalog_ProductDetail as Product, OfferResult} from '~/openapi';
import {useShoppingCartStore} from '~/stores/shoppingCart';
import {useShoppingListsStore} from '~/stores/shoppingLists';

const routeToPageTypeMap: Record<string, string> = {
	index: 'homepage',
	'branch-hypermarket-akcni-nabidka-category': 'product_category',
	'branch-hypermarket-cela-nabidka-category': 'product_category',
	'branch-hypermarket-akcni-nabidka-p-product': 'product',
	'branch-hypermarket-akcni-nabidka-category-p-product': 'product',
	'branch-hypermarket-cela-nabidka-p-product': 'product',
	'branch-hypermarket-cela-nabidka-category-p-product': 'product',
	'o-nas': 'about_us',
	'o-nas-osobni-udaje-slug': 'personal_info',
	'o-nas-slug': 'about_us',
	'o-nas-tiskove-zpravy-slug': 'about_us',
	'o-nas-tiskove-zpravy': 'about_us',
	'muj-ucet': 'profile',
	'muj-ucet-penezni-bonus': 'profile',
	'muj-ucet-prehled-uctenek': 'profile',
	'muj-ucet-kupony': 'profile',
	'muj-ucet-kupony-kupon-id': 'profile',
	'muj-ucet-kupony-odmena-id': 'profile',
	'vernostni-program': 'my_globus',

	recepty: 'recipe',
	'recepty-category-slug': 'recipe',
	'recepty-category': 'recipe_category',
	'recepty-autori': 'recipe_author',
	'recepty-autori-slug': 'recipe_author',

	blog: 'blog',
	'blog-category-slug': 'article',
	'blog-category': 'article_category',

	'branch-online-objednavka': 'checkout',
	'branch-letaky': 'leaflets_list',
	'branch-letaky-slug': 'leaflet',
	'branch-online-objednavka-slug': 'checkout',
	'branch-online-objednavka-slug-shrnuti': 'cart',
	'branch-online-objednavka-slug-produkt-offerId': 'product',
	'branch-sluzby': 'service_category',
	'branch-sluzby-restaurace': 'restaurant',
	'branch-sluzby-slug': 'service',
	'branch-sluzby-obchodni-jednotky-k-pronajmu': 'business_unit',
	'branch-sluzby-obchodni-jednotky-k-pronajmu-slug': 'business_unit',

	'nakupni-seznamy': 'shopping_list',
	'nakupni-seznamy-id': 'shopping_list',
};

export function getGtmPageType(routeTo?: RouteLocationNormalized) {
	const route = routeTo ?? useRoute();
	const error = useError();

	if (error.value && 'statusCode' in error.value && (error.value.statusCode === 404 || error.value.statusCode === 429 || error.value.statusCode === 500)) {
		return `error_${error.value.statusCode}`;
	}

	return routeToPageTypeMap[route.name as string] || route.name;
}

export function mapProductsToGtmEvent(products: Product[]) {
	return products.map((product) => ({
		item_id: product.vanr,
		item_name: product.name,
		affiliation: 'Globus.cz',
		item_brand: product.brand?.name,
		quantity: 1,
		price: product.productInHouse
			? getNormalPrice(product.productInHouse)
			: null,
		price_discount: product.productInHouse
			? getCurrentPrice(product.productInHouse)
			: null,
		price_GB: product.productInHouse?.bonusProgramPrice?.actualPrice ?? null,
		...product.productCategories?.reduce((acc, category, index) => ({
			...acc,
			[`item_category${computerToHumanIndexing(index)}`]: category,
		}), {}),
	}));
}

export function mapGoosProductsToGtmEvent(products: OfferResult[], category?: string | null, trackCartQuantity?: boolean) {
	const shoppingCartStore = useShoppingCartStore();
	const {itemsById} = storeToRefs(shoppingCartStore);

	return products.map((product) => ({
		item_id: product.offerId,
		item_name: product.mealNameOffer,
		affiliation: 'Globus.cz',
		item_category: category ?? undefined,
		price: product.price?.price,
		quantity: trackCartQuantity ? itemsById.value[product?.offerId ?? 0] : 1,
	}));
}

export function trackEcommerceEvent(event: string, ecommercePayload: any) {
	const gtm = useGtm();
	gtm?.push({ecommerce: null});
	gtm?.push({
		event,
		ecommerce: ecommercePayload,
	});
}

export type ShoppingListAddMethod
	= | 'product_cart'
	| 'search_result'
	| 'shopping_list_search_result'
	| 'product_detail'
	| 'DL_detail'
	| 'DL_modal';

export type ShoppingListItemType = 'product' | 'text';

/** Guest lists use negative internal IDs; GTM expects a stable `guest_<id>` identifier. */
export function getShoppingListIdForGtm(listId: number | string | null | undefined): number | string | null {
	if (listId == null || listId === '') {
		return null;
	}

	const numericId = Number(listId);
	if (!Number.isFinite(numericId) || numericId >= 0) {
		return numericId;
	}

	const guestList = useShoppingListsStore().getGuestListsState().lists
		.find((list) => list.ID === numericId);

	return guestList?.gtmUuid ? `guest_${guestList.gtmUuid}` : null;
}

interface TrackShoppingListAddItemOptions {
	product?: Product;
	method: ShoppingListAddMethod;
	itemType: ShoppingListItemType;
	listId: number | string | null;
	uniqItems: number;
}

/** Push the `shopping_list_add_item` event when a unique item is first added to a shopping list. */
export function trackShoppingListAddItem({
	product,
	method,
	itemType,
	listId,
	uniqItems,
}: TrackShoppingListAddItemOptions) {
	const gtm = useGtm();
	if (!gtm) {
		return;
	}

	gtm.push({ecommerce: null});
	gtm.push({
		event: 'shopping_list_add_item',
		shopping_list_id: getShoppingListIdForGtm(listId),
		shopping_list_uniq_items: uniqItems,
		method,
		item_type: itemType,
		page_type: getGtmPageType(),
		...(itemType === 'product' && product
			? {ecommerce: {items: mapProductsToGtmEvent([product])}}
			: {}),
	});
}

/** Push Luigi's Box customer_id collector event after login */
export function trackLuigisBoxCustomerId(customerId: string) {
	const gtm = useGtm();
	gtm?.push({
		event: 'luigisbox.collector.customer_id',
		customer_id: customerId,
	});
}

export function trackHeaderEvent(content: string, text?: string, payload: Record<string, unknown> = {}) {
	const gtm = useGtm();
	gtm?.push({
		event: 'main_menu_click',
		content,
		text,
		...payload,
	});
}

export function trackLinkClickEvent(e: MouseEvent) {
	const gtm = useGtm();
	const target = e.target as HTMLAnchorElement;

	gtm?.push({
		event: 'click',
		type: 'link',
		text: target.textContent,
		target: target.href,
		page_type: getGtmPageType(),
	});
}

/** Luigi's Box loader script when `public.luigisBoxId` is set and speed mode is off. */
function buildLuigisBoxHeadScript(runtimeConfig: any, isSpeedMode: Ref<boolean>) {
	const rawId = String(runtimeConfig.public.luigisBoxId ?? '').trim();
	if (!rawId || isSpeedMode.value) {
		return null;
	}
	const scriptFileBase = rawId.startsWith('LBX-') ? rawId : `LBX-${rawId}`;
	return {
		hid: 'luigis-box',
		src: `https://scripts.luigisbox.tech/${scriptFileBase}.js`,
		async: true as const,
	};
}

/**
 * Marketing and analytics script tags for `useHead` (GTM, ads, chatbot, Luigi's Box, …).
 * @param runtimeConfig — Nuxt runtime config
 * @param isSpeedMode — ref to speed mode (lighter devices / data saving)
 */
export function createMarketingHeadData(runtimeConfig: any, isSpeedMode: Ref<boolean>) {
	const luigisBoxScript = buildLuigisBoxHeadScript(runtimeConfig, isSpeedMode);
	const scripts = [
		runtimeConfig.public.googleAdsCode && !isSpeedMode.value
			? {
				crossorigin: 'anonymous' as const,
				src: `https://pagead2.googlesyndication.com/pagead/js/adsbygoogle.js?client=${runtimeConfig.public.googleAdsCode}`,
				async: true as const,
			}
			: undefined,
		runtimeConfig.public.chatbotEnabled && typeof runtimeConfig.public.chatbotEnabled === 'boolean' && !isSpeedMode.value
			? {
				innerHTML: `
					var daktelaGuiConfig = {
						"server":"https://globus.daktela.com/",
						"accessToken":"96q76q8n29rs36nr7pspor46430os2o4"
					};
					var daktelaScriptEl = document.createElement("script");
					var daktelaNonceEl = document.currentScript;
					var daktelaNonce = daktelaNonceEl && (daktelaNonceEl.nonce || daktelaNonceEl.getAttribute("nonce"));
					if (daktelaNonce) daktelaScriptEl.setAttribute("nonce", daktelaNonce);
					(function (attrs) { Object.keys(attrs).forEach(function (key) { daktelaScriptEl.setAttribute(key, attrs[key]); }); })
					({"src":daktelaGuiConfig.server+"external/web/web.js", "type":"text/javascript", "async":true, "charset":"utf-8"});
					daktelaScriptEl.onload = function() { if (!daktelaGui) return; var daktelaWeb = new daktelaGui(); daktelaWeb.init(daktelaGuiConfig); };
					document.getElementsByTagName("head")[0].appendChild(daktelaScriptEl);
				`,
			}
			: undefined,
		...(!isSpeedMode.value
			? [
				{
					key: 'GTM-script',
					innerHTML: `
					(function(w,d,s,l,i){w[l]=w[l]||[];w[l].push({'gtm.start':
					new Date().getTime(),event:'gtm.js'});var f=d.getElementsByTagName(s)[0],
					j=d.createElement(s),dl=l!='dataLayer'?'&l='+l:'';j.async=true;j.src=
					'https://www.googletagmanager.com/gtm.js?id='+i+dl;var n=d.currentScript||d.querySelector('[nonce]');
					n&&j.setAttribute('nonce',n.nonce||n.getAttribute('nonce'));f.parentNode.insertBefore(j,f);
					})(window,document,'script','dataLayer','GTM-NCTHX82');
					`,
				},
				{
					key: 'pubads',
					src: 'https://securepubads.g.doubleclick.net/tag/js/gpt.js',
					async: true as const,
				},
			]
			: []),
		...(luigisBoxScript ? [luigisBoxScript] : []),
	].filter((script): script is NonNullable<typeof script> => Boolean(script));

	const noscript = [
		!isSpeedMode.value
			? {
				key: 'GTM-noscript',
				innerHTML: '<iframe src="https://www.googletagmanager.com/ns.html?id=GTM-NCTHX82" height="0" width="0" style="display:none;visibility:hidden"></iframe>',
				tagPosition: 'bodyOpen' as const,
			}
			: undefined,
	].filter((entry): entry is NonNullable<typeof entry> => Boolean(entry));

	return {scripts, noscript};
}

/**
 * Push a synthetic `page_view` to the dataLayer (optionally including error details).
 * @param error — optional error payload for error pages
 */
export function trackPageViewError(error?: {
	url: string;
	statusCode: number;
	statusMessage: string;
	message: string;
	stack: string;
}) {
	const gtm = useGtm();

	if (!gtm) {
		return;
	}

	const pageType = error ? `error_${error.statusCode}` : getGtmPageType();

	gtm.push({
		event: 'page_view',
		page_type: pageType,
		url: import.meta.client ? window.location.href : '',
		...(error && {error}),
	});
}
